import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import {
  useGetSingleSalesChannelForAuthorityQuery,
  useGetSingleSalesChannelInfoQuery
} from 'Services/SalesManagement/Features/TicketSale/salesChannelApi'
import type { TicketSale } from 'Services/SalesManagement/Features/TicketSale/types'
import { datetimeFormatter } from 'Util/formatters'
import { CircularProgress, Paper, Table, TableBody, TableCell, TableContainer } from '@mui/material'
import { styled } from '@mui/material/styles'
import { t } from 'ttag'

interface TicketSaleDetailsProps {
  ticketSale: TicketSale
}

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
`

export default function TicketSaleDetails({ ticketSale }: Readonly<TicketSaleDetailsProps>) {
  const { getAuthorityNameByCode } = useAuthorityContext()
  const { data: singleSalesChannel } = useGetSingleSalesChannelForAuthorityQuery({
    authorityCode: ticketSale.authority
  })
  const { data: salesChannelInfo, isFetching: isSalesChannelInfoFetching } =
    useGetSingleSalesChannelInfoQuery(
      {
        salesChannelId: singleSalesChannel?.external_id as string
      },
      {
        skip: !singleSalesChannel?.external_id
      }
    )
  const getAgeGroupNameById = (id: number) => {
    const ageGroup = salesChannelInfo?.age_groups.find(ageGroup => ageGroup.id === id)
    return ageGroup ? ageGroup.name : '?'
  }
  return (
    <TableContainer component={Paper}>
      <Table sx={{ td: { border: 'none' } }} aria-label={t`Details table`}>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell>{t`Transaction ID`}</StyledTableCell>
            <TableCell>{ticketSale.transaction_id}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Target identifier`}</StyledTableCell>
            <TableCell>{ticketSale.target_identifier}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Target email`}</StyledTableCell>
            <TableCell>{ticketSale.target_email}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Product`}</StyledTableCell>
            <TableCell>{ticketSale.product_name}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Zone from`}</StyledTableCell>
            <TableCell>{ticketSale.zone_from_name}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Zone to`}</StyledTableCell>
            <TableCell>{ticketSale.zone_to_name}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Validity starts`}</StyledTableCell>
            <TableCell>{datetimeFormatter(ticketSale.validity_starts)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Validity ends`}</StyledTableCell>
            <TableCell>{datetimeFormatter(ticketSale.validity_ends)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Age group`}</StyledTableCell>
            <TableCell>
              {isSalesChannelInfoFetching ? (
                <CircularProgress size={18} />
              ) : (
                getAgeGroupNameById(ticketSale.age_group_id)
              )}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Group size`}</StyledTableCell>
            <TableCell>{ticketSale.group_size}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Authority`}</StyledTableCell>
            <TableCell>{getAuthorityNameByCode(ticketSale.authority)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Created`}</StyledTableCell>
            <TableCell>{datetimeFormatter(ticketSale.created)}</TableCell>
          </StyledTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}
